import { Frustum, Ray, Vector3 } from 'three';

import { CesiumStore } from '../../../../stores/geodata-store-factory/cesium-point-cloud-store/cesium-point-cloud-store';
import { isDefined } from '../../../../utils/variables';
import { Cache } from '../../../cache/cache-manager';
import { SkyviewCamera } from '../../../cameras/types';
import { TileNode, TileSet } from './types';
import {
  createTileNodeDataUri,
  evaluateTileNodeForCulling,
  extractTileNodeBoundingSphere,
  isTileSetNode,
  recalculateCameraHeight,
} from './utilities';

export interface TileNodeLoadResponse {
  node: TileNode;
  index: number;
  distanceToRay: number;
}

export type View = {
  camera: SkyviewCamera;
  worldPosition: Vector3;
  frustum: Frustum;
  offset: Vector3;
  ray: Ray;
};

/**
 * Queues eligible chunks for loading based on a few criteria:
 *
 * - The chunk has data
 * - All parent chunks are loaded (or lacks data)
 * - The chunk is within the cameras range and frustum.
 *
 * For data sets without any top LOD-level the function will find nested chunks
 * with data exhaustively for every path in the tree.
 */
export async function queueChunksToLoad(
  tileNode: TileNode,
  index: number,
  cache: Cache,
  view: View,
  cesiumPointCloudStore: CesiumStore,
  chunksToLoad: TileNodeLoadResponse[],
  parentHasData: boolean,
) {
  view.worldPosition.setZ(recalculateCameraHeight(view.camera, tileNode, view.offset));

  /**
   *  Evaluate if any of the children are within the camera view.
   *
   *  Also identify which children are loaded and which are not.
   */
  let isAChildWithinCameraView = false;
  const childrenNotLoaded = [];
  const loadedChildren = [];
  for (const childNode of tileNode.children || []) {
    const { withinRange, withinFrustum } = evaluateTileNodeForCulling(
      view.worldPosition,
      view.frustum,
      childNode,
      view.offset,
    );

    // Skip loading if this node is outside of the cameras frustrum, and the parent has data.
    if ((parentHasData || isDefined(tileNode.content?.uri)) && !withinFrustum) {
      continue;
    }

    isAChildWithinCameraView =
      isAChildWithinCameraView || !parentHasData || (withinRange && withinFrustum);

    let curChildNode = childNode;
    let childNodeUrl = curChildNode.content?.uri
      ? createTileNodeDataUri(curChildNode, curChildNode.content.uri)
      : undefined;

    if (childNodeUrl && isTileSetNode(curChildNode)) {
      if (!cache.isCached(childNodeUrl)) {
        cache.queue(childNodeUrl);
        const tileSet = await cesiumPointCloudStore.fetchTileSet(childNodeUrl);
        // TODO: Rename this to "cesium" since it is used both by pointcloud and tiled models.
        cache.addPointcloud(childNodeUrl, tileSet);
      }
      // If a child is cached and is a tileset, evaluate the tileset root node.
      const tileSetChild = cache.get(childNodeUrl) as TileSet;
      curChildNode = tileSetChild.root;

      // TODO: It is possible that nested tilesets also have empty root nodes.
      // Not a case we have encountered yet. But we may take it into consideration in the future.
      childNodeUrl = createTileNodeDataUri(
        curChildNode,
        curChildNode.content?.uri as unknown as string,
      );
    }

    // If a child is not cached or queued, add to not loaded queue.
    if (childNodeUrl && !(cache.isCached(childNodeUrl) || cache.isQueued(childNodeUrl))) {
      childrenNotLoaded.push(curChildNode);
    } else {
      // If cached and loaded or if it doesnt have content, add to loaded queue.
      loadedChildren.push(curChildNode);
    }
  }

  /**
   *  Evaluate which nodes are to be loaded.
   *
   *  If any child is within the camera view and all siblings are loaded, traverse down.
   *  Also traverse down if this node does not have any data.
   */
  if (
    (isAChildWithinCameraView && childrenNotLoaded.length === 0) ||
    (!isAChildWithinCameraView && !isDefined(tileNode.content))
  ) {
    for (const childNode of loadedChildren || []) {
      await queueChunksToLoad(
        childNode,
        index + 1,
        cache,
        view,
        cesiumPointCloudStore,
        chunksToLoad,
        isDefined(tileNode.content?.uri) || parentHasData,
      );
    }
  } else if (isAChildWithinCameraView) {
    // If any child is within camera view, but not all siblings are loaded,
    // load the remaining siblings.
    for (const childNode of childrenNotLoaded) {
      const sphere = extractTileNodeBoundingSphere(childNode);

      chunksToLoad.push({
        node: childNode,
        index,
        distanceToRay: view.ray.distanceSqToPoint(sphere.center),
      });
    }
  }
}
